import React, { useEffect, useMemo, useState, useCallback, useDeferredValue } from "react";
import { useMedia } from "react-use";
import { CSSTransition } from "react-transition-group";
import $ from "jquery";
import Map from "./Map";
import StoreList from "./StoreList";
import SearchBox from "./SearchBox";
import ModalCitySelector from "../ModalCitySelecter";
import Reserve from "./Reserve/";
import ReserveConfirmPhone from "./ReserveConfirmPhone/";
import * as api from "../api";
import Buy from "./Buy/Buy";
import Spinner from "../Spinner";

const tabs = {
  MAP: "map",
  LIST: "list",
};
const orderSteps = {
  SELECT_STORE: "selectStore",
  CART: "cart",
  RESERVE_PRODUCT: "reserveProduct",
  CONFIRM_PHONE: "confirmPhone",
  LOADING: "loading",
};

export default function StoreSelector({ productId }) {
  const { phrases } = window;
  const [itemInStores, setItemInStores] = useState(null);
  const [showCitySelectorModal, setShowCitySelectorModal] = useState(false);
  const [activeTab, setActiveTab] = useState(tabs.LIST);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filterQueryString, setFilterQueryString] = useState(null);
  const [city, setCity] = useState(null);
  const [cityCode, setCityCode] = useState(0);
  const [orderStep, setOrderStep] = useState(orderSteps.LOADING);
  const [errorMessage, setErrorMessage] = useState(null);
  const isDesktop = useMedia("(min-width: 961px)");
  const [hasMapMounted, setHasMapMounted] = useState(false);
  const [mapSelectedStoreId, setMapSelectedStoreId] = useState(null);
  
  const deferredSelectedStoreId = useDeferredValue(selectedStoreId);

  const handleStoreSelect = useCallback((storeId) => {
    setSelectedStoreId(storeId);
  }, []);

  const onCityClick = ({ code, name }) => {
    if (city === name) return;
    if (code === 0) {
      setCity(null);
    }
    setCityCode(code);
    setCity(name);
  };

  const cityList = useMemo(() => {
    if (!itemInStores) return null;
    const tmpList = itemInStores.data.reduce(
      (acc, item) =>
        item.city_name && acc.indexOf(item.city_name) === -1
          ? [...acc, item.city_name]
          : acc,
      [phrases.shops.all_Ukraine]
    );
    return tmpList
      // .sort((a, b) => {
      //   if (a !== tmpList[0] && b !== tmpList[0]) {
      //     return a.localeCompare(b);
      //   }
      //   return 0;
      // })
      .map((item, index) => ({
        name: item,
        id: index,
        code: index,
      }));
  }, [itemInStores]);

  const storesListByCity = useMemo(() => {
    if (!itemInStores) return null;
    return itemInStores.data.filter((item) => {
      if (!item.address || !item.coordinates || !item.identifier) return false;
      if (
        cityCode === 0 ||
        item.city_name.toLocaleLowerCase() === city.toLocaleLowerCase()
      ) {
        return true;
      }
      return false;
    });
  }, [itemInStores, city]);

  const storesList = useMemo(() => {
    if (!storesListByCity) return null;
    return storesListByCity.filter((item) => {
      if (!filterQueryString) return true;
      if (
        filterQueryString &&
        item.address.toLowerCase().includes(filterQueryString)
      )
        return true;
      return false;
    });
  }, [storesListByCity, filterQueryString]);

  const storesMap = useMemo(() => {
    if (!storesList) return new window.Map();
    const lookupMap = new window.Map();
    storesList.forEach(store => {
      lookupMap.set(store.identifier, store);
    });
    return lookupMap;
  }, [storesList]);

  const selectedStoreProduct = useMemo(() => {
    if (!selectedStoreId || !storesMap.size) return storesListByCity?.[0]?.item;
    return storesMap.get(selectedStoreId)?.item || storesListByCity?.[0]?.item;
  }, [selectedStoreId, storesMap, storesListByCity]);

  const pointsList = useMemo(() => {
    if (!storesList) return [];
    return storesList.map(({ address, coordinates, identifier, schedule }) => {
      const [lat, lng] = coordinates.split(", ");
      return {
        id: identifier,
        address,
        lat: Number(lat),
        lng: Number(lng),
        schedule,
      };
    });
  }, [storesList]);

  const openTab = (e) => {
    setActiveTab(e.currentTarget.dataset.tab);
    // window.google.maps.event.trigger(map, 'resize');
  };

  useEffect(() => {
    document.querySelector("html").classList.add("overflow");
    $(".modal-store-selector").fadeIn(300);
    // async () => {return await api.getStoresList()}
    // setOrderStep(orderSteps.LOADING)
    api.getStoresList({ id: productId }).then((data) => {
      if (!data?.data || data.data.length === 0) {
        setErrorMessage(
          phrases?.product?.["store_selector_error_empty_list"]
        );
      } else {
        setErrorMessage(null);
      }
      const sortedStores = data.data?.sort((a,b) => {
        if (a.city_id === 1) {
          return -1
        }
        if (b.city_id === 1) {
          return 1
        }
        return a.city_name?.localeCompare(b.city_name)
      })
      setItemInStores({data: sortedStores || []});
      setOrderStep(orderSteps.SELECT_STORE);
    }).catch(() => {
      setErrorMessage(phrases?.product?.["store_selector_error_empty_list"]);
      setItemInStores({data: []});
      setOrderStep(orderSteps.SELECT_STORE);
    });

    return () => {
      document.querySelector("html").classList.remove("overflow");
    };
  }, []);

  useEffect(() => {
    if (storesListByCity) setSelectedStoreId(storesListByCity[0]?.identifier);
  }, [storesListByCity]);

  useEffect(() => {
    if ((storesList && selectedStoreId) || storesListByCity) {
      setSelectedProduct(
        storesList?.find(({ identifier }) => identifier === selectedStoreId)
          ?.item || storesListByCity[0]?.item
      );
    }
  }, [storesList, selectedStoreId, storesListByCity]);

  // On mobile: Update mapSelectedStoreId when switching to MAP tab
  // This ensures the Map only updates when it's visible
  useEffect(() => {
    if (!isDesktop && activeTab === tabs.MAP) {
      // Mark that Map has been shown at least once
      setHasMapMounted(true);
      // Update the selected store for the Map
      setMapSelectedStoreId(selectedStoreId);
    }
  }, [isDesktop, activeTab, selectedStoreId]);

  // Determine if Map should be rendered on mobile:
  // - First render: only when MAP tab is active
  // - After first mount: keep it mounted (but hidden when LIST tab is active)
  const shouldRenderMap = isDesktop || activeTab === tabs.MAP || hasMapMounted;
  
  // Determine which selectedPoint to pass to Map:
  // - On desktop: use deferredSelectedStoreId (always visible, can use deferred)
  // - On mobile: use mapSelectedStoreId (only updates when MAP tab is active)
  const mapSelectedPoint = isDesktop ? deferredSelectedStoreId : mapSelectedStoreId;


  switch (orderStep) {
    // case orderSteps.LOADING:
    //   return <div
    //   className={`modal modal-store-selector js_store_selector_modal ${activeTab}`}
    //   style={{ display: "block" }}
    // >
    //   <div className="modal__zoom">
    //     <div className="modal-content">
    //       <div className="close-btn js-close" />
    //       </div>
    //       </div>
    //       </div>
    case orderSteps.CART:
      return null;
    // case orderSteps.RESERVE_PRODUCT:
    //   return (
    //     <CSSTransition
    //       in
    //       appear
    //       unmountOnExit
    //       classNames="fade"
    //       timeout={3000}
    //     >
    //       <Reserve product={selectedProduct} storeId={selectedStoreId} />
    //     </CSSTransition>
    //   );
    case orderSteps.CONFIRM_PHONE:
      return <ReserveConfirmPhone />;
    default:
      return (
        <>
          {/* Store selector */}
          <CSSTransition
            in={
              orderStep === orderSteps.LOADING ||
              orderStep === orderSteps.SELECT_STORE
            }
            appear
            unmountOnExit
            classNames="fade"
            timeout={300}
          >
            <div
              className={`modal modal-store-selector js_store_selector_modal ${activeTab}`}
              style={{ display: "block" }}
            >
              <div className="modal__zoom">
                <div className="modal-content">
                  <div className="close-btn js-close" />
                  <div className="modal-store-selector__head flex-c">
                    <div className="modal-content__title">
                      {phrases?.product?.["store_selector_title"] ||
                        "Select store"}
                    </div>
                    <div
                      onClick={() => setShowCitySelectorModal(true)}
                      className="city-search change-sity"
                    >
                      <div className="city-search__current flex-c">
                        {phrases.shops.select_city}
                        <div className="current">
                          <span>{city || phrases.shops.all_Ukraine}</span>

                          <svg className="icon-arrow">
                            <use xlinkHref="#arrow" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-store-selector__content">
                    <div className="store-selector__search">
                      <SearchBox setQueryString={setFilterQueryString} />
                    </div>
                    <div className="store-selector__tabs flex-c" role="tablist">
                      <button
                        type="button"
                        onClick={openTab}
                        data-tab={tabs.LIST}
                        className={`tabs__item js_store_tab ${activeTab ===
                          tabs.LIST && "active"}`}
                      >
                        {phrases?.shops?.adresses || "Addresses stores"}
                      </button>
                      <button
                        type="button"
                        onClick={openTab}
                        data-tab={tabs.MAP}
                        className={`tabs__item js_store_tab ${activeTab ===
                          tabs.MAP && "active"}`}
                      >
                        {phrases?.shops?.["on_map"] || "On the map"}
                      </button>
                    </div>
                    {/* {activeTab === tabs.LIST ? ( */}

                    <div className="store-selector__store-list">
                      {orderStep === orderSteps.LOADING ? (
                        <Spinner />
                      ) : (
                        <CSSTransition
                          in
                          appear
                          unmountOnExit
                          classNames="fade"
                          timeout={300}
                        >
                          <StoreList
                            error={errorMessage}
                            storeList={storesList}
                            setSelectedStore={handleStoreSelect}
                            selectedStore={selectedStoreId}
                          />
                        </CSSTransition>
                      )}
                    </div>

                    <div className="store-selector__map" style={!isDesktop && activeTab !== tabs.MAP ? { display: 'none' } : undefined}>
                      {shouldRenderMap && (
                        <Map selectedPoint={mapSelectedPoint} setSelectedPoint={handleStoreSelect} pointsList={pointsList} />
                      )}
                    </div>

                    <Buy
                      goCart={() => {
                        setOrderStep(orderSteps.CART);
                      }}
                      goReserve={() => {
                        setOrderStep(orderSteps.RESERVE_PRODUCT);
                      }}
                      product={selectedStoreProduct}
                    />
                    
                  </div>
                </div>
              </div>
            </div>
          </CSSTransition>
          
          {/* City selector */}
          <CSSTransition
            in={showCitySelectorModal}
            appear
            unmountOnExit
            classNames="fade"
            timeout={300}
          >
            <ModalCitySelector
              value=""
              isVisible
              onCityClick={onCityClick}
              toggleModal={() => {
                setShowCitySelectorModal(false);
              }}
              cityList={cityList}
              title={phrases.shops.choose_city}
              inputPlaceholder={phrases.shops.shop_search}
            />
          </CSSTransition>

          {/* Reserve product */}
          <CSSTransition
            in={orderStep === orderSteps.RESERVE_PRODUCT}
            appear
            unmountOnExit
            classNames="fade"
            timeout={300}
          >
            <Reserve product={selectedProduct} storeId={selectedStoreId} />
          </CSSTransition>
        </>
      );
  }
}
